/**
 * @file DriversManager.js
 * @version 1.3.4
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-01-19
 * @modified 2026-10-01
 * @description Manage drivers locally. Inline editing for name + email + per-row
 *              editors for phones (array), hauliers (M:N to transport.hauliers),
 *              default vehicle (FK to transport.vehicles) and EU tachograph card
 *              (which names the driver on the RIO fleet map).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import './SearchLayout.css';
import './TableStyles.css';
import './DriversManager.css';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import AuditHistoryModal from './AuditHistoryModal';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import MultiSelectDropdown from './MultiSelectDropdown';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import { hasPermission } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import { useReveal } from '../utils/revealInSearch';

// What Clear Filters resets the search box and archived toggle to.
const CLEARED_FILTERS = { search: '', showArchived: false };

function DriversManager({ user }) {
  const [drivers, setDrivers] = useState([]);
  const [hauliers, setHauliers] = useState([]); // [{id, name}]
  const [vehicles, setVehicles] = useState([]); // [{id, registration}]
  const [loading, setLoading] = useState(true);
  // Arriving from a modal's "Find in …" (utils/revealInSearch.js): searched for.
  const reveal = useReveal('transport-drivers');
  const [searchTerm, setSearchTerm] = useState(reveal?.term || '');
  const [showArchived, setShowArchived] = useState(false);

  // Per-field edit state — only one row/field active at a time.
  const [editing, setEditing] = useState({ id: null, field: null });
  const [draftName, setDraftName] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [draftPhones, setDraftPhones] = useState(['']);
  const [draftHaulierIds, setDraftHaulierIds] = useState([]);
  const [draftVehicleId, setDraftVehicleId] = useState('');
  const [draftTachoCard, setDraftTachoCard] = useState('');
  const [tachoCards, setTachoCards] = useState([]);
  const [saving, setSaving] = useState(false);

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhones, setNewPhones] = useState(['']);
  const [newHaulierIds, setNewHaulierIds] = useState([]);
  const [newVehicleId, setNewVehicleId] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalSaving, setModalSaving] = useState(false);

  // History modal state
  const [showHistory, setShowHistory] = useState(false);
  const [historyDriver, setHistoryDriver] = useState(null);

  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: null });

  useSlideOutButton([showAddModal, modalSaving]);

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers?includeArchived=${showArchived}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) setDrivers(data.drivers);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setAlertDialog({ isOpen: true, message: 'Failed to load drivers', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  const fetchHauliers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setHauliers(data.hauliers.map(h => ({ id: h.id, name: h.name })));
    } catch (err) {
      console.error('Error fetching hauliers:', err);
    }
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/transport/vehicles`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setVehicles(data.vehicles.map(v => ({ id: v.id, registration: v.registration })));
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    }
  }, []);

  // Tacho cards the RIO feed has actually seen. RIO identifies the driver of a
  // MAN truck by EU tachograph card number only, so assigning a card here is
  // what makes a name appear on the fleet map instead of "UK DB232833...".
  const fetchTachoCards = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers/tacho-cards`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setTachoCards(data.cards || []);
    } catch (err) {
      console.error('Error fetching tacho cards:', err);
    }
  }, []);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);
  useEffect(() => { fetchHauliers(); fetchVehicles(); fetchTachoCards(); }, [fetchHauliers, fetchVehicles, fetchTachoCards]);

  const filteredDrivers = drivers.filter(d => matchesAllTerms([
    d.name,
    d.email,
    ...(d.phones || []),
    ...(d.hauliers || []).map(h => h.name),
    d.default_vehicle_registration,
    d.tacho_card,
  ], searchTerm));

  const ctxMenu = useTableContextMenu({
    rows: filteredDrivers,
    getRowId: (d) => d.id
  });

  if (!hasPermission(user, 'transport_planning')) {
    return <AccessDenied permission="Transport Planning" />;
  }

  // ── Inline edit helpers ──────────────────────────────────────
  const startEdit = (driver, field) => {
    if (editing.id) return; // one at a time
    setEditing({ id: driver.id, field });
    if (field === 'name') setDraftName(driver.name);
    if (field === 'email') setDraftEmail(driver.email || '');
    if (field === 'phones') setDraftPhones(driver.phones && driver.phones.length > 0 ? [...driver.phones] : ['']);
    if (field === 'hauliers') setDraftHaulierIds((driver.hauliers || []).map(h => h.id));
    if (field === 'vehicle') setDraftVehicleId(driver.default_vehicle_id ? String(driver.default_vehicle_id) : '');
    if (field === 'tachoCard') setDraftTachoCard(driver.tacho_card || '');
  };

  const cancelEdit = () => {
    setEditing({ id: null, field: null });
    setDraftName('');
    setDraftEmail('');
    setDraftPhones(['']);
    setDraftHaulierIds([]);
    setDraftVehicleId('');
    setDraftTachoCard('');
  };

  const isEditing = (driver, field) => editing.id === driver.id && editing.field === field;

  const updateDriverLocal = (driverId, updates) => {
    setDrivers(prev => prev.map(d => d.id === driverId ? { ...d, ...updates } : d));
  };

  // ── Save handlers ────────────────────────────────────────────
  const saveName = async (driverId) => {
    const trimmed = draftName.trim();
    if (!trimmed) return;
    const current = drivers.find(d => d.id === driverId);
    if (trimmed === current?.name) { cancelEdit(); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers/${driverId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed })
      });
      const data = await res.json();
      if (data.success) { updateDriverLocal(driverId, data.driver); cancelEdit(); }
      else setAlertDialog({ isOpen: true, message: data.error || 'Failed to update name', type: 'error' });
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update name', type: 'error' });
    } finally { setSaving(false); }
  };

  const saveEmail = async (driverId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers/${driverId}/email`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: draftEmail.trim() || null })
      });
      const data = await res.json();
      if (data.success) { updateDriverLocal(driverId, data.driver); cancelEdit(); }
      else setAlertDialog({ isOpen: true, message: data.error || 'Failed to update email', type: 'error' });
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update email', type: 'error' });
    } finally { setSaving(false); }
  };

  const savePhones = async (driverId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers/${driverId}/phones`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phones: draftPhones })
      });
      const data = await res.json();
      if (data.success) { updateDriverLocal(driverId, data.driver); cancelEdit(); }
      else setAlertDialog({ isOpen: true, message: data.error || 'Failed to update phones', type: 'error' });
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update phones', type: 'error' });
    } finally { setSaving(false); }
  };

  const saveHauliers = async (driverId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers/${driverId}/hauliers`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ haulierIds: draftHaulierIds })
      });
      const data = await res.json();
      if (data.success) { updateDriverLocal(driverId, data.driver); cancelEdit(); }
      else setAlertDialog({ isOpen: true, message: data.error || 'Failed to update hauliers', type: 'error' });
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update hauliers', type: 'error' });
    } finally { setSaving(false); }
  };

  const saveVehicle = async (driverId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers/${driverId}/default-vehicle`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vehicleId: draftVehicleId ? parseInt(draftVehicleId, 10) : null })
      });
      const data = await res.json();
      if (data.success) { updateDriverLocal(driverId, data.driver); cancelEdit(); }
      else setAlertDialog({ isOpen: true, message: data.error || 'Failed to update default vehicle', type: 'error' });
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update default vehicle', type: 'error' });
    } finally { setSaving(false); }
  };

  const saveTachoCard = async (driverId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers/${driverId}/tacho-card`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tachoCard: draftTachoCard || null })
      });
      const data = await res.json();
      if (data.success) {
        updateDriverLocal(driverId, data.driver);
        cancelEdit();
        // Refresh so the card drops out of the unassigned list.
        fetchTachoCards();
      } else {
        // 409 means the card is already held by another driver.
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to update tacho card', type: 'error' });
      }
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update tacho card', type: 'error' });
    } finally { setSaving(false); }
  };

  // ── Archive ──────────────────────────────────────────────────
  const handleArchive = (driver) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Archive Driver',
      message: `Archive "${driver.name}"? They will no longer appear in transport planning or PO dropdowns.`,
      type: 'warning',
      confirmText: 'Archive',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const res = await fetch(`${API_URL}/api/transport/drivers/${driver.id}`, {
            method: 'DELETE', credentials: 'include'
          });
          const data = await res.json();
          if (data.success) {
            if (showArchived) updateDriverLocal(driver.id, { status: 'ARCHIVED' });
            else setDrivers(prev => prev.filter(d => d.id !== driver.id));
          } else setAlertDialog({ isOpen: true, message: data.error || 'Failed to archive driver', type: 'error' });
        } catch (err) {
          setAlertDialog({ isOpen: true, message: 'Failed to archive driver', type: 'error' });
        }
      }
    });
  };

  // ── Add driver ───────────────────────────────────────────────
  const handleOpenAdd = () => {
    setNewName(''); setNewEmail(''); setNewPhones(['']); setNewHaulierIds([]); setNewVehicleId('');
    setModalError(''); setShowAddModal(true);
  };

  const handleSaveNew = async () => {
    const trimmed = newName.trim();
    if (!trimmed) { setModalError('Driver name is required'); return; }
    if (trimmed.length > 100) { setModalError('Name must be 100 characters or less'); return; }

    setModalSaving(true);
    setModalError('');
    try {
      const res = await fetch(`${API_URL}/api/transport/drivers`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmed,
          email: newEmail.trim() || undefined,
          phones: newPhones.filter(p => p && p.trim()),
          haulierIds: newHaulierIds,
          defaultVehicleId: newVehicleId ? parseInt(newVehicleId, 10) : null
        })
      });
      const data = await res.json();
      if (data.success) {
        setDrivers(prev => [...prev, data.driver].sort((a, b) => a.name.localeCompare(b.name)));
        setShowAddModal(false);
      } else {
        setModalError(data.error || 'Failed to create driver');
      }
    } catch (err) {
      setModalError('Failed to create driver. Please try again.');
    } finally {
      setModalSaving(false);
    }
  };

  // ── Phones editor (used inline + in add modal) ───────────────
  const renderPhonesEditor = (phones, setPhones, disabled) => (
    <div className="dm-phones-editor">
      {phones.map((p, i) => (
        <div key={i} className="dm-phone-row">
          <input
            type="tel"
            className="dm-edit-input"
            value={p}
            onChange={(e) => {
              const next = [...phones]; next[i] = e.target.value; setPhones(next);
            }}
            placeholder="Phone number"
            disabled={disabled}
          />
          <button
            type="button"
            className="btn-icon btn-danger"
            title="Delete"
            onClick={() => {
              const next = phones.filter((_, idx) => idx !== i);
              setPhones(next.length > 0 ? next : ['']);
            }}
            disabled={disabled}
          >
            <span className="material-symbols-rounded">delete</span>
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn-icon dm-phones-add"
        title="Add phone"
        onClick={() => setPhones([...phones, ''])}
        disabled={disabled}
      >
        <span className="material-symbols-rounded">add</span>
        <span style={{ marginLeft: 4, fontSize: 'var(--font-sm)', whiteSpace: 'nowrap' }}>Add phone</span>
      </button>
    </div>
  );

  // ── Render helpers ───────────────────────────────────────────
  const haulierOptions = hauliers.map(h => ({ value: h.id, label: h.name }));
  const vehicleOptions = [
    { value: '', label: 'Haulier' },
    ...vehicles.map(v => ({ value: String(v.id), label: v.registration }))
  ];

  // Cards the RIO feed has seen that nobody holds yet, plus whichever card this
  // driver already holds (so the dropdown can show the current value). Labelled
  // with the truck it was last seen in, which is how you tell one 19-character
  // card number from another.
  const tachoCardOptions = (driver) => [
    { value: '', label: 'Driver' },
    ...tachoCards
      .filter(c => !c.driver_id || c.driver_id === driver.id)
      .map(c => ({
        value: c.tacho_card,
        label: c.last_vehicle ? `${c.tacho_card} — last in ${c.last_vehicle}` : c.tacho_card,
        searchText: `${c.tacho_card} ${c.last_vehicle || ''}`
      }))
  ];

  const buildRowActions = (driver) => [
    { key: 'history', icon: 'history', label: 'History', title: 'View history', onClick: () => { setHistoryDriver(driver); setShowHistory(true); } },
    ...(driver.status === 'ACTIVE' ? [
      { key: 'archive', icon: 'archive', label: 'Archive', title: 'Archive driver', className: 'btn-danger', onClick: () => handleArchive(driver) },
    ] : []),
  ];

  // Any header filter applied lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(
    { search: searchTerm, showArchived }, CLEARED_FILTERS, { labels: { showArchived: 'Archived' } }
  );
  const filtersApplied = headerFilters.count > 0;

  return (
    <div className="search-layout drivers-manager" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Manage Drivers</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>Manage drivers, their phones, the hauliers they work for and a default vehicle for auto-fill.</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={() => { setSearchTerm(CLEARED_FILTERS.search); setShowArchived(CLEARED_FILTERS.showArchived); }}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleOpenAdd}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add Driver</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr auto' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['name', 'email', 'phone', 'haulier', 'default vehicle', 'tacho card']} />
            </label>
            <Input
              type="text"
              placeholder="Search by name, email, phone, haulier or vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-archived-drivers"
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                <label htmlFor="show-archived-drivers"></label>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="search-content">
        <div className="table-container">
          {loading ? (
            <LoadingSpinner />
          ) : filteredDrivers.length === 0 ? (
            <div className="no-results">
              <span className="material-symbols-rounded">badge</span>
              <p>{searchTerm ? 'No matching drivers found' : 'No drivers available. Click "Add Driver" to create one.'}</p>
            </div>
          ) : (
            <table className="search-results-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phones</th>
                  <th>Hauliers</th>
                  <th>Default Vehicle</th>
                  <th>Tacho Card</th>
                  <th className="status-col">Status</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {filteredDrivers.map(driver => (
                  <tr
                    key={driver.id}
                    className={driver.status !== 'ACTIVE' ? 'dm-archived-row' : ''}
                    onContextMenu={ctxMenu.onRowContextMenu(driver)}
                  >
                    {/* Name */}
                    <td className="dm-name-cell">
                      {isEditing(driver, 'name') ? (
                        <div className="dm-inline-edit">
                          <input
                            type="text" className="dm-edit-input" value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveName(driver.id); if (e.key === 'Escape') cancelEdit(); }}
                            maxLength={100} autoFocus disabled={saving}
                          />
                          <button className="btn-icon dm-btn-save" onClick={() => saveName(driver.id)} disabled={saving} title="Save"><span className="material-symbols-rounded">check</span></button>
                          <button className="btn-icon" onClick={cancelEdit} disabled={saving} title="Cancel"><span className="material-symbols-rounded">close</span></button>
                        </div>
                      ) : (
                        <span className="dm-name-display" onClick={() => startEdit(driver, 'name')} title="Click to edit name">
                          <strong>{driver.name}</strong>
                        </span>
                      )}
                    </td>

                    {/* Email */}
                    <td className="dm-email-cell">
                      {isEditing(driver, 'email') ? (
                        <div className="dm-inline-edit">
                          <input
                            type="email" className="dm-edit-input" value={draftEmail}
                            onChange={(e) => setDraftEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveEmail(driver.id); if (e.key === 'Escape') cancelEdit(); }}
                            placeholder="Enter email" autoFocus disabled={saving}
                          />
                          <button className="btn-icon dm-btn-save" onClick={() => saveEmail(driver.id)} disabled={saving} title="Save"><span className="material-symbols-rounded">check</span></button>
                          <button className="btn-icon" onClick={cancelEdit} disabled={saving} title="Cancel"><span className="material-symbols-rounded">close</span></button>
                        </div>
                      ) : (
                        <span className={`dm-email-display ${!driver.email ? 'dm-empty' : ''}`} onClick={() => startEdit(driver, 'email')} title="Click to edit email">
                          {driver.email || 'Click to add email'}
                        </span>
                      )}
                    </td>

                    {/* Phones */}
                    <td className="dm-phones-cell">
                      {isEditing(driver, 'phones') ? (
                        <div className="dm-inline-edit dm-inline-edit-stacked">
                          {renderPhonesEditor(draftPhones, setDraftPhones, saving)}
                          <div className="dm-inline-actions">
                            <button className="btn-icon dm-btn-save" onClick={() => savePhones(driver.id)} disabled={saving} title="Save"><span className="material-symbols-rounded">check</span></button>
                            <button className="btn-icon" onClick={cancelEdit} disabled={saving} title="Cancel"><span style={{ color: 'var(--color-error)' }} className="material-symbols-rounded">close</span></button>
                          </div>
                        </div>
                      ) : (
                        <span className={`dm-email-display ${!driver.phones || driver.phones.length === 0 ? 'dm-empty' : ''}`} onClick={() => startEdit(driver, 'phones')} title="Click to edit phones">
                          {driver.phones && driver.phones.length > 0 ? driver.phones.join(', ') : 'Click to add phones'}
                        </span>
                      )}
                    </td>

                    {/* Hauliers */}
                    <td className="dm-hauliers-cell">
                      {isEditing(driver, 'hauliers') ? (
                        <div className="dm-inline-edit dm-inline-edit-stacked">
                          <MultiSelectDropdown
                            options={haulierOptions}
                            value={draftHaulierIds}
                            onChange={(ids) => setDraftHaulierIds(ids)}
                            placeholder="Select hauliers..."
                            emptyMessage="No hauliers available"
                          />
                          <div className="dm-inline-actions">
                            <button className="btn-icon dm-btn-save" onClick={() => saveHauliers(driver.id)} disabled={saving} title="Save"><span className="material-symbols-rounded">check</span></button>
                            <button className="btn-icon" onClick={cancelEdit} disabled={saving} title="Cancel"><span className="material-symbols-rounded">close</span></button>
                          </div>
                        </div>
                      ) : (
                        <span className={`dm-email-display ${!driver.hauliers || driver.hauliers.length === 0 ? 'dm-empty' : ''}`} onClick={() => startEdit(driver, 'hauliers')} title="Click to edit hauliers">
                          {driver.hauliers && driver.hauliers.length > 0
                            ? driver.hauliers.map(h => h.name).join(', ')
                            : 'Click to add hauliers'}
                        </span>
                      )}
                    </td>

                    {/* Default Vehicle */}
                    <td className="dm-vehicle-cell">
                      {isEditing(driver, 'vehicle') ? (
                        <div className="dm-inline-edit dm-inline-edit-stacked">
                          <SearchableDropdown
                            options={vehicleOptions}
                            value={draftVehicleId}
                            onChange={(v) => setDraftVehicleId(v)}
                            placeholder="Select vehicle..."
                            searchPlaceholder="Search..."
                          />
                          <div className="dm-inline-actions">
                            <button className="btn-icon dm-btn-save" onClick={() => saveVehicle(driver.id)} disabled={saving} title="Save"><span className="material-symbols-rounded">check</span></button>
                            <button className="btn-icon" onClick={cancelEdit} disabled={saving} title="Cancel"><span className="material-symbols-rounded">close</span></button>
                          </div>
                        </div>
                      ) : (
                        <span className={`dm-email-display ${!driver.default_vehicle_registration ? 'dm-empty' : ''}`} onClick={() => startEdit(driver, 'vehicle')} title="Click to set default vehicle">
                          {driver.default_vehicle_registration || 'Click to set default'}
                        </span>
                      )}
                    </td>

                    <td className="dm-tacho-cell">
                      {isEditing(driver, 'tachoCard') ? (
                        <div className="dm-inline-edit dm-inline-edit-stacked">
                          <SearchableDropdown
                            options={tachoCardOptions(driver)}
                            value={draftTachoCard}
                            onChange={(v) => setDraftTachoCard(v)}
                            placeholder="Select card..."
                            searchPlaceholder="Search..."
                          />
                          <div className="dm-inline-actions">
                            <button className="btn-icon dm-btn-save" onClick={() => saveTachoCard(driver.id)} disabled={saving} title="Save"><span className="material-symbols-rounded">check</span></button>
                            <button className="btn-icon" onClick={cancelEdit} disabled={saving} title="Cancel"><span className="material-symbols-rounded">close</span></button>
                          </div>
                        </div>
                      ) : (
                        <span className={`dm-email-display ${!driver.tacho_card ? 'dm-empty' : ''}`} onClick={() => startEdit(driver, 'tachoCard')} title="Click to assign the EU tachograph card that identifies this driver on the fleet map">
                          {driver.tacho_card || 'Click to assign card'}
                        </span>
                      )}
                    </td>

                    <td className="status-col">
                      <span className={`status-badge ${driver.status === 'ACTIVE' ? 'status-active' : 'status-archived'}`}>
                        {driver.status}
                      </span>
                    </td>

                    <td className="actions-col">
                      {!editing.id && (
                        <RowActionsMenu actions={buildRowActions(driver)} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Driver Modal */}
      {showAddModal && createPortal(
        <div className="modal-overlay" onClick={() => !modalSaving && setShowAddModal(false)}>
          <div className="modal-content modal-sm drivers-manager" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Driver</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)} disabled={modalSaving}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: "0 var(--spacing-xl) var(--spacing-xl)" }}>
              {modalError && (
                <div className="dm-modal-error">{modalError}</div>
              )}
              <div className="form-group">
                <label>Driver Name <span style={{ color: 'var(--color-error)' }}>*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter driver name"
                  maxLength={100}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Email <span className="dm-label-hint">(optional)</span></label>
                <input
                  type="email"
                  className="form-control"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
              <div className="form-group">
                <label>Phones <span className="dm-label-hint">(optional, one or more)</span></label>
                {renderPhonesEditor(newPhones, setNewPhones, modalSaving)}
              </div>
              <div className="form-group">
                <label>Hauliers <span className="dm-label-hint">(optional, used for driver auto-fill)</span></label>
                <MultiSelectDropdown
                  options={haulierOptions}
                  value={newHaulierIds}
                  onChange={(ids) => setNewHaulierIds(ids)}
                  placeholder="Select hauliers..."
                  emptyMessage="No hauliers available"
                />
              </div>
              <div className="form-group">
                <label>Default Vehicle <span className="dm-label-hint">(optional, used for vehicle auto-fill)</span></label>
                <SearchableDropdown
                  options={vehicleOptions}
                  value={newVehicleId}
                  onChange={(v) => setNewVehicleId(v)}
                  placeholder="Select vehicle..."
                  searchPlaceholder="Search..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button data-read-safe className="btn-round btn-danger" onClick={() => setShowAddModal(false)} disabled={modalSaving}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel</span>
              </button>
              <button className="btn-slideout" onClick={handleSaveNew} disabled={modalSaving || !newName.trim()}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">{modalSaving ? 'hourglass_top' : 'save'}</span></div>
                <span className="btn-label">{modalSaving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {showHistory && historyDriver && (
        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => { setShowHistory(false); setHistoryDriver(null); }}
          entityType="driver"
          entityId={historyDriver.id}
          entityIdentifier={historyDriver.name}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        onConfirm={() => { if (confirmDialog.onConfirm) confirmDialog.onConfirm(); }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ isOpen: false, message: '', type: 'info' })}
      />

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add driver', onClick: handleOpenAdd },
          { key: 'refresh', icon: 'refresh', label: 'Refresh', onClick: fetchDrivers },
          {
            key: 'show-archived',
            icon: 'inventory_2',
            label: 'Show archived',
            active: showArchived,
            onClick: () => setShowArchived(v => !v)
          },
          {
            key: 'clear-filters',
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: () => { setSearchTerm(''); setShowArchived(false); }
          }
        ]}
      />
    </div>
  );
}

export default DriversManager;
